module Meevo.Plugins.AppointmentBook {
    export class ProductUsageModalController {
        public originalServices: Meevo.Model.IProductUsageService[];
        public services: Meevo.Model.IProductUsageService[];
        
        public variantCriteria = {
            enableProductUsageTracking: true,
            includeNotForSale: true,
            explicitProductInventoryTypes: [
                Meevo.Model.ProductInventoryTypeEnum.ShopSupply,
                Meevo.Model.ProductInventoryTypeEnum.RetailOrShopSupply
            ],
        };

        static $inject = [
            "appointmentServiceId",
            "clientDisplayName",
            "$rootScope",
            "$translate",
            "meevo.AppSettings",
            "meevo.components.common.DataService",
            "meevo.components.modal.ModalService",
        ];

        constructor(
            private appointmentServiceId: string,
            public clientDisplayName: string,
            private $rootScope: angular.IRootScopeService,
            private $translate: angular.translate.ITranslateService,
            private appSettings: Meevo.Components.Common.IAppSettings,
            private dataService: Meevo.Components.BaseClasses.IDataService,
            private modalService: Meevo.Components.Modal.IModalService,            
        ) {
            this.initialize(appointmentServiceId);
        }

        private initialize(appointmentServiceId: string) {
            return this.getProductUsage(appointmentServiceId);
        }

        private rebuildExcludedVariants(service: Meevo.Model.IProductUsageService) {
            service.excludedVariants = [];

            for (const variant of service.variants) {
                service.excludedVariants.push({ id: variant.variantId });
            }
        }

        private getProductUsage(appointmentServiceId: string) {
            return this.dataService.customMethodData(`/api/appointmentService/${appointmentServiceId}/productUsage`, "GET", null)
                .then(response => {
                    this.originalServices = angular.copy(response.data.services);
                    this.services = response.data.services;
                    
                    const request = {
                        productUsages: [],
                    };

                    for (const service of this.services) {
                        service.isExpanded = true;

                        for (const variant of service.variants) {
                            request.productUsages.push({
                                serviceId: service.serviceId,
                                variantId: variant.variantId,
                                usageAmount: variant.usageAmount,
                            });                            
                        }

                        this.rebuildExcludedVariants(service);
                    }
        
                    return this.getProductUsageCharge(request).then(usageCharges => this.populateUsageCharges(usageCharges));
                });
        }

        getUnitOfMeasure(unitOfMeasure: Meevo.Model.UnitEnum, productUsageAmountType: Meevo.Model.ProductUsageAmountTypeEnum): string {
            switch (productUsageAmountType) {
                case Meevo.Model.ProductUsageAmountTypeEnum.PartialAmount:
                    return this.$translate.instant("ENUM_LABEL_" + unitOfMeasure) + "(s)";
                case Meevo.Model.ProductUsageAmountTypeEnum.WholeProduct:
                    return this.$translate.instant("LABEL_PRODUCT_USAGE_WHOLE_PRODUCT");
            }
        }

        isWholeProduct(productUsageAmountType: Meevo.Model.ProductUsageAmountTypeEnum): boolean {
            return productUsageAmountType === Meevo.Model.ProductUsageAmountTypeEnum.WholeProduct;
        }

        isValid() {
            if (_(this.services).chain().pluck("variants").flatten().any(variant => {
                return +variant.usageAmount <= 0;
            }).value()) {
                return false;
            }
            return true;
        }

        save() {
            if (!this.isValid()) {
                this.modalService.alert("MESSAGE_COMPLETE_ALL_FIELDS_THAT_REQUIRE_ATTENTION",
                    Components.Modal.ModalTranslationModes.Translate, true);
                return;
            }

            const request = {
                services: this.services
            };
            this.dataService.customMethodData(`/api/appointmentService/${this.appointmentServiceId}/productUsage`, "PUT", request).then(() => {
                this.close();
            });
        }

        close() {
            this.modalService.hide();
        }

        cancel() {
            this.modalService.hide();
        }

        private getProductUsageCharge(request) {
            return this.dataService.customMethodData(`/api/productUsage/price/list`, "POST", request)
                .then(response => {
                    return response.data;
                });
        }

        usageAmountChange(service: Meevo.Model.IProductUsageService, variant: Meevo.Model.IProductUsageVariant) {
            //comes in as a string which doesn't cause the required red border to show when changing to 0 so cast to a number
            variant.usageAmount = +variant.usageAmount;
            if (variant.usageAmount === variant.previousUsageAmount) {
                return;
            }

            variant.previousUsageAmount = variant.usageAmount;

            const request = {
                productUsages: [{
                    serviceId: service.serviceId,
                    variantId: variant.variantId,
                    usageAmount: variant.usageAmount,
                }],
            };

            return this.getProductUsageCharge(request).then(usageCharges => this.populateUsageCharges(usageCharges));
        }

        private populateUsageCharges(usageCharges) {
            for (var productUsage of usageCharges.productUsages) {
                const service = this.services.find(service => service.serviceId === productUsage.priceRequest.serviceId);
                if (!service) {
                    continue;
                }

                const variant = service.variants.find(variant => variant.variantId === productUsage.priceRequest.variantId);
                if (!variant) {
                    continue;
                }

                variant.usageCharge = productUsage.usageCharge;
            }
        }

        getVariantDisplayName(variant: Meevo.Model.IProductUsageVariant): string {
            return `${variant.productSku}: ${variant.productDisplayName}${(variant.variantDisplayName ? ' - ' + variant.variantDisplayName : '')}`;
        }

        productVariantChanged(service: Meevo.Model.IProductUsageService) {
            if (!service.newVariant) {
                return;
            }

            const originalService = this.originalServices.find((originalService) => originalService.serviceId == service.serviceId);           
            const originalVariant = originalService.variants.find((originalVariant) => originalVariant.variantId == service.newVariant.id);

            let variant: Meevo.Model.IProductUsageVariant = null;
            
            if (originalVariant) {
                variant = angular.copy(originalVariant);
            } else {
                variant = {
                    productId: service.newVariant.productId,
                    variantId: service.newVariant.id,
                    productSku: service.newVariant.sku,
                    productDisplayName: service.newVariant.displayName,
                    variantDisplayName: service.newVariant.variantDisplayName,
                    unitOfMeasure: service.newVariant.productUnitOfMeasureEnum,
                    productUsageAmountType: service.newVariant.productUsageType,
                    usageAmount: 0,
                    usageCharge: null,
                    chargeType: service.newVariant.chargeType
                };
            }

            service.variants.push(variant);
            service.variants = service.variants.sort((a, b) => a.productSku.toLowerCase().localeCompare(b.productSku.toLowerCase()));

            service.newVariant = null;

            this.rebuildExcludedVariants(service);
            this.usageAmountChange(service, variant);
        }

        isRevertVariantVisible(variant: Meevo.Model.IProductUsageVariant): boolean {
            return angular.isDefined(variant.defaultUsageAmount) && variant.defaultUsageAmount != variant.usageAmount;
        }

        revertVariant(service: Meevo.Model.IProductUsageService, variant: Meevo.Model.IProductUsageVariant) {
            variant.usageAmount = variant.defaultUsageAmount;

            this.usageAmountChange(service, variant);
        }

        removeVariant(service: Meevo.Model.IProductUsageService, variant: Meevo.Model.IProductUsageVariant) {
            service.variants = service.variants.filter((serviceVariant) => serviceVariant.variantId !== variant.variantId);

            this.rebuildExcludedVariants(service);
        }

        preProcessData(response, request) {
            if (request.pageNumber == 0 && response.length == 0) {
                this.modalService.alert('MESSAGE_PRODUCT_USAGE_NO_PRODUCTS', Components.Modal.ModalTranslationModes.Translate, true);
            }
        }
    }

    angular.module("ApptBookApp")
        .controller("meevo.plugins.appointmentBook.ProductUsageModalController", ProductUsageModalController);

}